import {
  Checkbox,
  ChoiceGroup,
  ComboBox,
  DefaultButton,
  IComboBox,
  IComboBoxOption,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  Icon,
  IChoiceGroupOption,
  IDropdownOption,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  TextField,
} from "@fluentui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";

import { useAmendRiskCheck } from "hooks/data/mutations/useAmendRiskCheck";
import { useAmendRiskExecute } from "hooks/data/mutations/useAmendRiskExecute";
import { useAmendRiskProposal } from "hooks/data/mutations/useAmendRiskProposal";
import {
  setPendingFlottomat,
  startTogetherConnect,
} from "pages/together/together";
import { getTogetherStatus } from "store/api/api";
import { setFlottomatDialog } from "store/fleetPage";
import { handleAxiosError } from "store/util";
import { useSelector } from "store/hooks";
import { AppDispatch } from "store/store";
import {
  AmendRiskCheckRequest,
  AmendRiskCheckResponse,
  AmendRiskProposalResponse,
  VinkularglaeubigerDto,
  ZusatzDeckungDto,
  ZusatzProduktDto,
  ZusatzProduktSelectionDto,
} from "types/types";
import { FormSpinner } from "./FormSpinner";
import { VSpace } from "./Spacer";

type DialogStep = "loading" | "form" | "checking" | "checked" | "executing" | "success" | "error";

interface ProductSelection {
  [productId: string]: {
    eingeschlossen: boolean;
    deckungen: { [deckungId: string]: boolean };
  };
}

export const FlottomatDialog = () => {
  const { t } = useTranslation();
  const theme = useTheme() as IStyledTheme;
  const dispatch: AppDispatch = useDispatch();

  const flottomatDialog = useSelector((s) => s.fleetPage.flottomatDialog);
  const { fleetId, vehicleId } = flottomatDialog;

  const [step, setStep] = useState<DialogStep>("loading");
  const [proposalData, setProposalData] = useState<AmendRiskProposalResponse | null>(null);
  const [selectedVariantLfNr, setSelectedVariantLfNr] = useState<number | null>(null);
  const [productSelections, setProductSelections] = useState<ProductSelection>({});
  const [wechselkennzeichen, setWechselkennzeichen] = useState(false);
  const [kundenkontonummer, setKundenkontonummer] = useState<string>("");
  const [vinkulierung, setVinkulierung] = useState(false);
  const [vinkularglaeubiger, setVinkularglaeubiger] = useState<VinkularglaeubigerDto>({});
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [checkResponse, setCheckResponse] = useState<AmendRiskCheckResponse | null>(null);
  const [geschaeftsfallnummer, setGeschaeftsfallnummer] = useState<string>("");

  const { mutate: fetchProposal, isLoading: isProposalLoading } = useAmendRiskProposal();
  const { mutate: checkAmendRisk, isLoading: isCheckLoading } = useAmendRiskCheck();
  const { mutate: executeAmendRisk, isLoading: isExecuteLoading } = useAmendRiskExecute();

  const close = useCallback(() => {
    dispatch(
      setFlottomatDialog({
        isOpen: false,
        fleetId: null,
        vehicleId: null,
      })
    );
  }, [dispatch]);

  const loadProposal = useCallback(() => {
    if (!fleetId || !vehicleId) return;
    fetchProposal(
      { fleetId, vehicleId },
      {
        onSuccess: (res) => {
          const data = res.data as AmendRiskProposalResponse;
          if (data.ergebnis !== "OK") {
            setErrorMessages(
              data.meldungen?.map((m) => m.errorMsg) || [t("bfm.flotomat.checkFailed.label")]
            );
            setStep("error");
            return;
          }
          setProposalData(data);
          setKundenkontonummer(data.kundenkonten?.[0]?.kundenkontonummer || "");
          if (data.varianten?.length > 0) {
            const firstVariant = data.varianten[0];
            setSelectedVariantLfNr(firstVariant.lfNr);
            initProductSelections(firstVariant.zusatzProdukte);
            setWechselkennzeichen(firstVariant.wechselkennzeichen);
          }
          setStep("form");
        },
        onError: () => {
          setErrorMessages([t("bfm.flotomat.checkFailed.label")]);
          setStep("error");
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleetId, vehicleId, fetchProposal, t]);

  // On mount, first check the TOGETHER connection status. When the integration is
  // disabled the backend reports connected=true and we load the proposal directly.
  // When it is enabled but the user is not connected, we remember the action and
  // redirect to the TOGETHER login; the callback resumes it (see TogetherCallback).
  useEffect(() => {
    if (!fleetId || !vehicleId) return;
    setStep("loading");
    getTogetherStatus()
      .then((status) => {
        if (status.connected) {
          loadProposal();
        } else {
          setPendingFlottomat(fleetId, vehicleId);
          startTogetherConnect().catch((err) => {
            handleAxiosError(err);
            setErrorMessages([t("bfm.flotomat.checkFailed.label")]);
            setStep("error");
          });
        }
      })
      .catch((err) => {
        handleAxiosError(err);
        setErrorMessages([t("bfm.flotomat.checkFailed.label")]);
        setStep("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleetId, vehicleId]);

  const initProductSelections = (zusatzProdukte: ZusatzProduktDto[]) => {
    const selections: ProductSelection = {};
    zusatzProdukte?.forEach((product) => {
      const deckungen: { [id: string]: boolean } = {};
      product.zusatzDeckungen?.forEach((d) => {
        deckungen[d.id] = d.eingeschlossen;
      });
      selections[product.id] = {
        eingeschlossen: product.eingeschlossen,
        deckungen,
      };
    });
    setProductSelections(selections);
  };

  const selectedVariant = useMemo(() => {
    return proposalData?.varianten?.find((v) => v.lfNr === selectedVariantLfNr);
  }, [proposalData, selectedVariantLfNr]);

  const variantOptions: IChoiceGroupOption[] = useMemo(() => {
    return (
      proposalData?.varianten?.map((v) => ({
        key: String(v.lfNr),
        text: v.bezeichnung,
      })) || []
    );
  }, [proposalData]);

  const kundenkontoOptions: IDropdownOption[] = useMemo(() => {
    return (
      proposalData?.kundenkonten?.map((k) => ({
        key: k.kundenkontonummer,
        text: `${k.kundenkontoInfoText} (${k.kundenkontonummer})`,
      })) || []
    );
  }, [proposalData]);

  const handleVariantChange = useCallback(
    (_ev: any, option: IChoiceGroupOption) => {
      const lfNr = Number(option.key);
      setSelectedVariantLfNr(lfNr);
      const variant = proposalData?.varianten?.find((v) => v.lfNr === lfNr);
      if (variant) {
        initProductSelections(variant.zusatzProdukte);
        setWechselkennzeichen(variant.wechselkennzeichen);
      }
    },
    [proposalData]
  );

  const handleProductToggle = useCallback(
    (productId: string, checked: boolean) => {
      setProductSelections((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          eingeschlossen: checked,
        },
      }));
    },
    []
  );

  const handleDeckungToggle = useCallback(
    (productId: string, deckungId: string, checked: boolean) => {
      setProductSelections((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          deckungen: {
            ...prev[productId]?.deckungen,
            [deckungId]: checked,
          },
        },
      }));
    },
    []
  );

  const buildRequestPayload = useCallback((): AmendRiskCheckRequest => {
    const zusatzProdukte: ZusatzProduktSelectionDto[] =
      selectedVariant?.zusatzProdukte?.map((product) => ({
        id: product.id,
        eingeschlossen: productSelections[product.id]?.eingeschlossen ?? product.eingeschlossen,
        zusatzDeckungen:
          product.zusatzDeckungen?.map((d) => ({
            id: d.id,
            eingeschlossen:
              productSelections[product.id]?.deckungen?.[d.id] ?? d.eingeschlossen,
          })) || [],
      })) || [];

    return {
      varianteLfNr: selectedVariantLfNr!,
      zusatzProdukte,
      wechselkennzeichen,
      kundenkontonummer,
      // The street is not captured in the GUI and is always sent empty
      vinkularglaeubiger: vinkulierung ? { ...vinkularglaeubiger, strasse: null } : undefined,
      // E-mail notification is not offered in the GUI and is always requested
      eMailBenachrichtigung: true,
    };
  }, [
    selectedVariant,
    selectedVariantLfNr,
    productSelections,
    wechselkennzeichen,
    kundenkontonummer,
    vinkulierung,
    vinkularglaeubiger,
  ]);

  // Flottomat requires bank and postcode as soon as any lienholder field is filled
  const vinkularglaeubigerValidation = useMemo(() => {
    const isFilled = (v?: string) => !!v?.trim();
    const anyFilled =
      vinkulierung &&
      [
        vinkularglaeubiger.bank,
        vinkularglaeubiger.plz,
        vinkularglaeubiger.vertragsnummer,
      ].some(isFilled);
    return {
      required: anyFilled,
      bankMissing: anyFilled && !isFilled(vinkularglaeubiger.bank),
      plzMissing: anyFilled && !isFilled(vinkularglaeubiger.plz),
    };
  }, [vinkulierung, vinkularglaeubiger]);

  const handleVinkularglaeubigerChange = useCallback(
    (field: keyof VinkularglaeubigerDto, value?: string) => {
      setVinkularglaeubiger((prev) => ({ ...prev, [field]: value ?? "" }));
    },
    []
  );

  const leasinggeberOptions: IComboBoxOption[] = useMemo(
    () => proposalData?.leasinggeber?.map((l) => ({ key: l.name, text: l.name })) || [],
    [proposalData]
  );

  // Picking a known lienholder fills in the postcode; free text only sets the name.
  // All fields stay editable afterwards.
  const handleLienholderBankChange = useCallback(
    (_ev: React.FormEvent<IComboBox>, option?: IComboBoxOption, _index?: number, value?: string) => {
      const known = option && proposalData?.leasinggeber?.find((l) => l.name === option.key);
      if (known) {
        setVinkularglaeubiger((prev) => ({
          ...prev,
          bank: known.name,
          plz: known.plz,
        }));
      } else {
        // Flottomat accepts at most 100 characters for the lienholder name
        handleVinkularglaeubigerChange("bank", (value ?? option?.text)?.slice(0, 100));
      }
    },
    [proposalData, handleVinkularglaeubigerChange]
  );

  const handleCheck = useCallback(() => {
    const payload = buildRequestPayload();
    setErrorMessages([]);
    setStep("checking");

    checkAmendRisk(
      { fleetId: fleetId!, vehicleId: vehicleId!, data: payload },
      {
        onSuccess: (res) => {
          const data = res.data;
          if (data.ergebnis !== "OK") {
            // NOK or a SOAP fault ("FAULT", e.g. lienholder bank not found): show Generali's messages
            // and stay on the form so the user can correct the input
            const messages: string[] = (data.meldungen || [])
              .map((m: any) => m.errorMsg)
              .filter(Boolean);
            setErrorMessages(messages.length > 0 ? messages : [t("bfm.flotomat.checkFailed.label")]);
            setStep("form");
            return;
          }
          // Check passed, show confirmation step
          setCheckResponse(data);
          setErrorMessages(
            data.meldungen?.map((m: any) => m.errorMsg) || []
          );
          setStep("checked");
        },
        onError: () => {
          setErrorMessages([t("bfm.flotomat.checkFailed.label")]);
          setStep("error");
        },
      }
    );
  }, [fleetId, vehicleId, buildRequestPayload]);

  const handleExecute = useCallback(() => {
    const payload = buildRequestPayload();
    setStep("executing");
    executeAmendRisk(
      { fleetId: fleetId!, vehicleId: vehicleId!, data: payload },
      {
        onSuccess: (res) => {
          const data = res.data;
          if (data.geschaeftsfallnummer) {
            setGeschaeftsfallnummer(data.geschaeftsfallnummer);
            setStep("success");
          } else {
            const messages: string[] = (data.meldungen || [])
              .map((m: any) => m.errorMsg)
              .filter(Boolean);
            setErrorMessages(messages.length > 0 ? messages : [t("bfm.flotomat.checkFailed.label")]);
            setStep("error");
          }
        },
        onError: () => {
          setErrorMessages([t("bfm.flotomat.checkFailed.label")]);
          setStep("error");
        },
      }
    );
  }, [fleetId, vehicleId, buildRequestPayload]);

  const isLoading = isProposalLoading || isCheckLoading || isExecuteLoading;

  const title = (
    <div style={{ display: "flex", alignItems: "center" }}>
      <Icon
        style={{
          color: theme.palette.themePrimary,
          fontSize: "25px",
          marginRight: "5px",
        }}
      />
      {t("bfm.generaliApplicationReport.label")}
    </div>
  );

  const renderVehicleInfo = () => {
    const fz = proposalData?.fahrzeug;
    if (!fz) return null;
    return (
      <div style={{ marginBottom: "10px", fontSize: "13px", color: "#666" }}>
        <strong>{t("bfm.flotomat.vehicleInfo.label")}:</strong>{" "}
        {[fz.marke, fz.handelsbez, fz.kennzeichen, fz.fahrgestnr].filter(Boolean).join(" | ")}
      </div>
    );
  };

  const renderProduct = (product: ZusatzProduktDto) => {
    const selection = productSelections[product.id];
    const isChecked = selection?.eingeschlossen ?? product.eingeschlossen;

    return (
      <div key={product.id} style={{ marginLeft: "20px", marginBottom: "6px" }}>
        <Checkbox
          label={product.bezeichnung}
          checked={isChecked}
          disabled={!product.einschlussAenderbar}
          onChange={(_ev, checked) => handleProductToggle(product.id, !!checked)}
        />
        {product.beschreibungTxt && (
          <div style={{ marginLeft: "26px", fontSize: "12px", color: "#888" }}>
            {product.beschreibungTxt}
          </div>
        )}
        {isChecked && product.zusatzDeckungen?.length > 0 && (
          <div style={{ marginLeft: "26px", marginTop: "4px" }}>
            {product.zusatzDeckungen.map((d: ZusatzDeckungDto) => (
              <div key={d.id} style={{ marginBottom: "4px" }}>
                <Checkbox
                  label={d.bezeichnung}
                  checked={selection?.deckungen?.[d.id] ?? d.eingeschlossen}
                  disabled={!d.einschlussAenderbar}
                  onChange={(_ev, checked) =>
                    handleDeckungToggle(product.id, d.id, !!checked)
                  }
                />
                {d.beschreibungTxt && (
                  <div style={{ marginLeft: "26px", fontSize: "12px", color: "#888" }}>
                    {d.beschreibungTxt}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderForm = () => (
    <>
      {renderVehicleInfo()}

      {errorMessages.length > 0 && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline>
          {errorMessages.map((msg, i) => (
            <div key={i}>{msg}</div>
          ))}
        </MessageBar>
      )}

      <VSpace height={10} />

      <Dropdown
        label={t("bfm.flotomat.premiumAccount.label")}
        required
        options={kundenkontoOptions}
        selectedKey={kundenkontonummer || undefined}
        onChange={(_ev, option) => option && setKundenkontonummer(option.key as string)}
      />

      <VSpace height={10} />

      <ChoiceGroup
        label={t("bfm.flotomat.variant.label")}
        options={variantOptions}
        selectedKey={selectedVariantLfNr !== null ? String(selectedVariantLfNr) : undefined}
        onChange={handleVariantChange}
      />

      {selectedVariant?.zusatzProdukte?.length > 0 && (
        <>
          <VSpace height={10} />
          <strong>{t("bfm.flotomat.additionalProducts.label")}</strong>
          <VSpace height={4} />
          {selectedVariant.zusatzProdukte.map(renderProduct)}
        </>
      )}

      <VSpace height={10} />

      <Checkbox
        label={t("bfm.flotomat.sharedLicensePlate.label")}
        checked={wechselkennzeichen}
        onChange={(_ev, checked) => setWechselkennzeichen(!!checked)}
      />

      <VSpace height={10} />

      <Checkbox
        label={t("bfm.flotomat.lienRestriction.label")}
        checked={vinkulierung}
        onChange={(_ev, checked) => setVinkulierung(!!checked)}
      />
      {vinkulierung && (
        <div style={{ marginLeft: "26px", marginTop: "4px" }}>
          <ComboBox
            label={t("bfm.flotomat.lienholderBank.label")}
            required={vinkularglaeubigerValidation.required}
            errorMessage={
              vinkularglaeubigerValidation.bankMissing ? t("greco.form.errors.required") : undefined
            }
            options={leasinggeberOptions}
            allowFreeform
            autoComplete="on"
            useComboBoxAsMenuWidth
            text={vinkularglaeubiger.bank || ""}
            selectedKey={
              leasinggeberOptions.some((o) => o.key === vinkularglaeubiger.bank)
                ? vinkularglaeubiger.bank
                : null
            }
            onChange={handleLienholderBankChange}
          />
          <TextField
            label={t("bfm.flotomat.lienholderPostcode.label")}
            required={vinkularglaeubigerValidation.required}
            errorMessage={
              vinkularglaeubigerValidation.plzMissing ? t("greco.form.errors.required") : undefined
            }
            value={vinkularglaeubiger.plz || ""}
            maxLength={5}
            onChange={(_ev, value) => handleVinkularglaeubigerChange("plz", value)}
          />
          <TextField
            label={t("bfm.flotomat.lienholderContractNumber.label")}
            value={vinkularglaeubiger.vertragsnummer || ""}
            maxLength={32}
            onChange={(_ev, value) => handleVinkularglaeubigerChange("vertragsnummer", value)}
          />
        </div>
      )}

      <VSpace height={15} />

      <DialogFooter>
        <PrimaryButton
          text={t("bfm.flotomat.check.label")}
          onClick={handleCheck}
          disabled={
            selectedVariantLfNr === null ||
            isLoading ||
            vinkularglaeubigerValidation.bankMissing ||
            vinkularglaeubigerValidation.plzMissing
          }
        />
        <DefaultButton onClick={close} text={t("bfm.close.label")} />
      </DialogFooter>
    </>
  );

  const renderChecked = () => {
    const selectedProducts = selectedVariant?.zusatzProdukte?.filter(
      (p) => productSelections[p.id]?.eingeschlossen
    );
    // Same rule as the backend: the lienholder is only sent when at least one field is filled
    const vinkularglaeubigerSummary = vinkulierung
      ? [
          vinkularglaeubiger.bank,
          vinkularglaeubiger.plz,
          vinkularglaeubiger.vertragsnummer,
        ]
          .map((v) => v?.trim())
          .filter(Boolean)
          .join(" | ")
      : "";
    return (
      <>
        <div style={{ marginBottom: "10px", fontSize: "13px" }}>
          <div>
            <strong>{t("bfm.flotomat.variant.label")}:</strong> {selectedVariant?.bezeichnung}
          </div>
          {selectedProducts && selectedProducts.length > 0 && (
            <div style={{ marginTop: "4px" }}>
              <strong>{t("bfm.flotomat.additionalProducts.label")}:</strong>
              {selectedProducts.map((p) => {
                const selectedDeckungen = p.zusatzDeckungen?.filter(
                  (d) => productSelections[p.id]?.deckungen?.[d.id]
                );
                return (
                  <div key={p.id} style={{ marginLeft: "10px" }}>
                    - {p.bezeichnung}
                    {selectedDeckungen && selectedDeckungen.length > 0 && (
                      <div style={{ marginLeft: "10px" }}>
                        {selectedDeckungen.map((d) => (
                          <div key={d.id}>- {d.bezeichnung}</div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: "4px" }}>
            <strong>{t("bfm.flotomat.sharedLicensePlate.label")}:</strong>{" "}
            {wechselkennzeichen ? t("greco.yes") : t("greco.no")}
          </div>
          <div>
            <strong>{t("bfm.flotomat.accountNumber.label")}:</strong> {kundenkontonummer}
          </div>
          <div>
            <strong>{t("bfm.flotomat.lienRestriction.label")}:</strong>{" "}
            {vinkularglaeubigerSummary ? t("greco.yes") : t("greco.no")}
          </div>
          {vinkularglaeubigerSummary && (
            <div style={{ marginLeft: "10px" }}>{vinkularglaeubigerSummary}</div>
          )}
        </div>

        {errorMessages.length > 0 && (
          <MessageBar messageBarType={MessageBarType.warning} isMultiline>
            {errorMessages.map((msg, i) => (
              <div key={i}>{msg}</div>
            ))}
          </MessageBar>
        )}

        <VSpace height={15} />

        <DialogFooter>
          <PrimaryButton
            text={t("bfm.flotomat.execute.label")}
            onClick={handleExecute}
            disabled={isLoading}
          />
          <DefaultButton onClick={close} text={t("bfm.close.label")} />
        </DialogFooter>
      </>
    );
  };

  const renderSuccess = () => (
    <>
      <MessageBar messageBarType={MessageBarType.success}>
        {t("bfm.flotomat.success.label")}
      </MessageBar>
      <VSpace height={15} />
      <div>
        <strong>{t("bfm.flotomat.businessCaseNumber.label")}:</strong>{" "}
        {geschaeftsfallnummer}
      </div>
      <VSpace height={15} />
      <DialogFooter>
        <DefaultButton onClick={close} text={t("bfm.close.label")} />
      </DialogFooter>
    </>
  );

  const renderError = () => (
    <>
      <MessageBar messageBarType={MessageBarType.error} isMultiline>
        {errorMessages.map((msg, i) => (
          <div key={i}>{msg}</div>
        ))}
      </MessageBar>
      <VSpace height={15} />
      <DialogFooter>
        <DefaultButton onClick={close} text={t("bfm.close.label")} />
      </DialogFooter>
    </>
  );

  return (
    <Dialog
      hidden={false}
      onDismiss={close}
      dialogContentProps={{
        type: DialogType.normal,
        title,
        showCloseButton: true,
      }}
      minWidth={600}
      modalProps={{
        isBlocking: true,
      }}
    >
      {step === "loading" && <FormSpinner />}
      {step === "form" && renderForm()}
      {step === "checking" && (
        <>
          {renderForm()}
          <FormSpinner />
        </>
      )}
      {step === "checked" && renderChecked()}
      {step === "executing" && (
        <>
          {renderChecked()}
          <FormSpinner />
        </>
      )}
      {step === "success" && renderSuccess()}
      {step === "error" && renderError()}
    </Dialog>
  );
};
